import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Star, Download, Zap, ChevronRight } from 'lucide-react';
import { useAuth } from './contexts/AuthContext';
import ApiService from './services/api';
import CommunityApiService from './services/communityApi';
import UnifiedHeader from './components/UnifiedHeader';

const ModelsLanding = () => {
  const navigate = useNavigate();
  const { session, loading: authLoading } = useAuth();

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedHardware, setSelectedHardware] = useState('');
  const [sortBy, setSortBy] = useState('trending');
  const [models, setModels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [hardwareTargets, setHardwareTargets] = useState([]);
  const [communityStats, setCommunityStats] = useState(null);

  // Initialize hardware deployment targets
  useEffect(() => {
    setHardwareTargets([
      { id: 'h100', name: 'H100', fullName: 'NVIDIA H100', emoji: '🟢', count: 0 },
      { id: 'a100', name: 'A100', fullName: 'NVIDIA A100', emoji: '🔵', count: 0 },
      { id: 'mi300x', name: 'MI300X', fullName: 'AMD MI300X', emoji: '🔴', count: 0 },
      { id: 'rtx4090', name: '4090', fullName: 'RTX 4090', emoji: '🟣', count: 0 },
      { id: 'cpu-intel', name: 'Xeon', fullName: 'Intel Xeon', emoji: '🔷', count: 0 },
      { id: 'cpu-amd', name: 'EPYC', fullName: 'AMD EPYC', emoji: '🟠', count: 0 },
      { id: 'mobile', name: 'Edge', fullName: 'Mobile/Edge', emoji: '📱', count: 0 }
    ]);
  }, []);

  // Load models from API
  const loadModels = useCallback(async () => {
    if (authLoading || !session?.access_token) {
      return;
    }

    try {
      setLoading(true);
      setError('');
      const response = await ApiService.getModelsWithVariants(session.access_token);

      const transformedModels = response.models.map(model => ({
        id: model.id,
        name: model.name,
        description: `${model.layer_count} layers`,
        base_model_type: model.base_model_type,
        model_family: model.model_family,
        hardware_targets: model.hardware_targets || [],
        available_hardware: model.available_hardware || [],
        total_hardware_variants: model.total_hardware_variants || 0,
        best_speedup: model.best_speedup || 1.0,
        avg_speedup: model.avg_speedup || 1.0,
        hardware: model.hardware || 'cpu-intel',
        likes: 0,
        downloads: Math.floor(Math.random() * 10000), // Mock data
        updated: new Date(model.created_at).toLocaleDateString(),
        layer_count: model.layer_count,
        status: model.best_speedup > 1 ? 'optimized' : (model.total_hardware_variants > 0 ? 'candidate' : 'unoptimized'),
        tags: [model.model_family || 'general', model.base_model_type || 'unknown'],
        origin_type: model.origin_type || 'local',
        origin_details: model.origin_details || {}
      }));

      setModels(transformedModels);

      // Update hardware counts
      const hardwareCounts = response.hardware_summary || {};
      transformedModels.forEach(model => {
        model.available_hardware.forEach(hw => {
          hardwareCounts[hw] = (hardwareCounts[hw] || 0) + 1;
        });
      });

      setHardwareTargets(prevTargets =>
        prevTargets.map(target => ({
          ...target,
          count: hardwareCounts[target.id] || 0
        }))
      );
    } catch (err) {
      setError(`Failed to load models: ${err.message}`);
      console.error('Error loading models:', err);
    } finally {
      setLoading(false);
    }
  }, [authLoading, session]);

  useEffect(() => {
    if (!authLoading && session?.access_token) {
      loadModels();
    }
  }, [authLoading, session, loadModels]);

  // Load community stats
  useEffect(() => {
    const loadCommunityStats = async () => {
      try {
        const result = await CommunityApiService.getCommunityStats();
        setCommunityStats(result);
      } catch (err) {
        console.warn('Failed to load community stats:', err);
      }
    };
    loadCommunityStats();
  }, []);

  // Filter and sort models
  const filteredModels = models.filter(model => {
    const matchesSearch = searchTerm === '' ||
      model.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      model.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
      model.tags?.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()));

    const matchesHardware = selectedHardware === '' ||
      model.available_hardware.includes(selectedHardware);

    return matchesSearch && matchesHardware;
  });

  const sortedModels = [...filteredModels].sort((a, b) => {
    switch (sortBy) {
      case 'trending':
        return b.downloads - a.downloads;
      case 'recent':
        return new Date(b.updated) - new Date(a.updated);
      case 'speedup':
        return b.best_speedup - a.best_speedup;
      case 'likes':
        return b.likes - a.likes;
      default:
        return 0;
    }
  });

  const handleModelClick = (model) => {
    if (model.total_hardware_variants === 1) {
      const hardware = model.available_hardware[0] || model.hardware;
      navigate(`/models/${model.id}/${hardware}`);
    } else {
      navigate(`/models/${model.id}`);
    }
  };

  // Get time ago string
  const getTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'today';
    if (diffDays === 1) return '1 day ago';
    if (diffDays < 30) return `${diffDays} days ago`;
    if (diffDays < 60) return '1 month ago';
    return `${Math.floor(diffDays / 30)} months ago`;
  };

  return (
    <div className="min-h-screen relative" style={{ fontFamily: 'Inter, sans-serif' }}>
      {/* Gradient Background */}
      <div className="fixed inset-0 bg-[#0b0f19]" />
      <div
        className="fixed inset-0"
        style={{
          background: 'radial-gradient(ellipse 80% 50% at 50% -20%, rgba(120, 119, 198, 0.15), transparent)'
        }}
      />

      <div className="relative">
        <UnifiedHeader
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          showSearch={true}
          showUpload={true}
          showAK47={true}
          onUploadSuccess={(result) => {
            console.log('Upload successful, refreshing models list...', result);
            loadModels();
          }}
          onAK47Complete={(results) => {
            console.log('AK47 mag dump completed:', results);
            loadModels();
          }}
        />

        <div className="flex">
          {/* Left Sidebar */}
          <aside className="w-72 border-r border-[#1f2937] min-h-screen pl-6 pr-3 py-4 flex-shrink-0">
          {/* Hardware Targets */}
          <div className="mb-8">
            <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">
              Hardware
            </h3>
            <div className="space-y-1">
              <button
                onClick={() => setSelectedHardware('')}
                className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors flex items-center justify-between ${
                  selectedHardware === ''
                    ? 'bg-[#1f2937] text-white'
                    : 'text-gray-400 hover:bg-[#151b27] hover:text-gray-300'
                }`}
              >
                <span>All</span>
                <span className="text-xs text-gray-500">{models.length}</span>
              </button>
              {hardwareTargets.filter(hw => hw.count > 0).map(hw => (
                <button
                  key={hw.id}
                  onClick={() => setSelectedHardware(hw.id === selectedHardware ? '' : hw.id)}
                  className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors flex items-center justify-between ${
                    selectedHardware === hw.id
                      ? 'bg-[#1f2937] text-white'
                      : 'text-gray-400 hover:bg-[#151b27] hover:text-gray-300'
                  }`}
                >
                  <span className="flex items-center gap-2">
                    <span>{hw.emoji}</span>
                    <span>{hw.name}</span>
                  </span>
                  <span className="text-xs text-gray-500">{hw.count}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Stats */}
          {communityStats && (
            <div className="border-t border-[#1f2937] pt-6">
              <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">
                Stats
              </h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between text-gray-400">
                  <span>Total Models</span>
                  <span className="text-white font-medium">{models.length}</span>
                </div>
                <div className="flex justify-between text-gray-400">
                  <span>Optimizations</span>
                  <span className="text-orange-400 font-medium">{communityStats.total_optimizations || 0}</span>
                </div>
              </div>
            </div>
          )}
        </aside>

          {/* Main Content */}
          <main className="flex-1 min-w-0">
            <div className="pl-3 pr-8 py-4">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
              <div>
                <h1 className="text-2xl font-semibold text-white mb-1">
                  Models {models.length > 0 && <span className="text-gray-500 font-normal">{models.length.toLocaleString()}</span>}
                </h1>
              </div>

              {/* Sort */}
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-400">Sort:</span>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="bg-[#1f2937] border border-[#374151] text-white rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
                >
                  <option value="trending">Trending</option>
                  <option value="recent">Recently Updated</option>
                  <option value="speedup">Best Speedup</option>
                  <option value="likes">Most Likes</option>
                </select>
              </div>
            </div>

            {/* Models List */}
            {loading ? (
              <div className="flex items-center justify-center py-20">
                <div className="text-center">
                  <div className="w-8 h-8 border-2 border-gray-700 border-t-orange-500 rounded-full animate-spin mb-4 mx-auto" />
                  <p className="text-gray-500 text-sm">Loading models...</p>
                </div>
              </div>
            ) : error ? (
              <div className="bg-[#1f2937] border border-[#374151] rounded-lg p-8 text-center">
                <h3 className="text-lg font-semibold text-white mb-2">Error loading models</h3>
                <p className="text-gray-400 mb-4">{error}</p>
                <button
                  onClick={loadModels}
                  className="bg-orange-500 hover:bg-orange-600 text-white px-6 py-2 rounded-md font-medium transition-colors"
                >
                  Try Again
                </button>
              </div>
            ) : sortedModels.length === 0 ? (
              <div className="bg-[#1f2937] border border-[#374151] rounded-lg p-8 text-center">
                <h3 className="text-lg font-semibold text-white mb-2">No models found</h3>
                <p className="text-gray-400">
                  {models.length === 0
                    ? "Upload your first model to get started."
                    : "Try adjusting your filters."}
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                {sortedModels.map((model) => (
                  <div
                    key={model.id}
                    className="border border-[#1f2937] rounded-lg px-6 py-4 hover:bg-[#151b27] cursor-pointer transition-colors"
                    onClick={() => handleModelClick(model)}
                  >
                    <div className="flex items-start justify-between gap-4">
                      {/* Left: Model Info */}
                      <div className="flex-1 min-w-0">
                        {/* Model Name */}
                        <div className="flex items-center gap-2 mb-2">
                          <h3 className="text-base font-medium text-white hover:text-orange-400 transition-colors truncate">
                            {model.name}
                          </h3>
                          {model.best_speedup > 1 && (
                            <span className="inline-flex items-center gap-1 bg-green-500/10 text-green-400 px-2 py-0.5 rounded text-xs font-medium">
                              <Zap className="w-3 h-3" />
                              {model.best_speedup.toFixed(1)}x
                            </span>
                          )}
                        </div>

                        {/* Metadata Row */}
                        <div className="flex items-center gap-3 text-sm text-gray-400">
                          <span className="flex items-center gap-1">
                            <span className="text-gray-500">📊</span>
                            {model.model_family || 'Model'}
                          </span>
                          <span>•</span>
                          <span>{model.layer_count} layers</span>
                          <span>•</span>
                          <span>Updated {getTimeAgo(model.updated)}</span>
                          <span>•</span>
                          <span className="flex items-center gap-1">
                            <Download className="w-3 h-3" />
                            {model.downloads.toLocaleString()}
                          </span>
                          <span>•</span>
                          <span className="flex items-center gap-1">
                            <Zap className="w-3 h-3" />
                            {model.total_hardware_variants || 0}
                          </span>
                          <span>•</span>
                          <span className="flex items-center gap-1">
                            <Star className="w-3 h-3" />
                            {model.likes}
                          </span>
                        </div>
                      </div>

                      {/* Right: Arrow */}
                      <ChevronRight className="w-5 h-5 text-gray-600 flex-shrink-0 mt-1" />
                    </div>
                  </div>
                ))}
              </div>
            )}

              {/* Results Count */}
              {!loading && !error && sortedModels.length > 0 && (
                <div className="mt-6 text-center">
                  <p className="text-sm text-gray-500">
                    Showing {sortedModels.length} of {models.length} models
                  </p>
                </div>
              )}
            </div>
          </main>
        </div>
      </div>
    </div>
  );
};

export default ModelsLanding;
